import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"
import { Building2, LogOut, Moon, Shield, Sun, Users } from "lucide-react"
import InlineSVG from "react-inlinesvg"

import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  useTheme,
} from "@atlant/ui"

import { Loader } from "#components/loader.tsx"
import { AdminSectionProvider, useAdminSection } from "#lib/admin-section.tsx"
import { useAuth } from "#lib/auth.tsx"
import { isPortalAdmin } from "#utils/roles.ts"

import Logo from "/icons/atlant-logo.svg"

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: "/sign-in", search: { redirect: location.pathname } })
    }
    if (!isPortalAdmin(context.auth.user?.role ?? "")) {
      throw redirect({ to: "/access-denied" })
    }
  },
  component: AuthenticatedLayout,
})

const NAV_ITEMS = [
  { label: "Users", icon: Users, id: "users" as const },
  { label: "Organizations", icon: Building2, id: "organizations" as const },
  { label: "Portal Access", icon: Shield, id: "portal" as const },
]

function AuthenticatedLayout() {
  const { user, isPending, logout } = useAuth()

  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader className="text-primary size-12 animate-[spin_1.2s_linear_infinite]" />
      </div>
    )
  }

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : (user?.email?.slice(0, 2).toUpperCase() ?? "AD")

  const handleLogout = async () => {
    await logout()
    window.location.href = "/sign-in"
  }

  return (
    <AdminSectionProvider>
      <div className="flex h-screen">
        <Sidebar
          initials={initials}
          userName={user?.name}
          userEmail={user?.email}
          onLogout={() => void handleLogout()}
        />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </AdminSectionProvider>
  )
}

function Sidebar({
  initials,
  userName,
  userEmail,
  onLogout,
}: {
  initials: string
  userName?: string | null
  userEmail?: string | null
  onLogout: () => void
}) {
  const { section, setSection } = useAdminSection()
  const { theme, setTheme } = useTheme()

  return (
    <aside className="border-border flex w-60 shrink-0 flex-col border-r">
      <div className="flex items-center gap-2 px-5 pt-5 pb-4">
        <div className="h-7 w-28">
          <InlineSVG src={Logo} className="h-full w-auto" />
        </div>
        <span className="text-muted-foreground text-xs font-medium">Admin Portal</span>
      </div>

      <Separator />

      <nav className="flex flex-1 flex-col gap-0.5 p-3">
        <p className="text-muted-foreground mb-1 px-2 text-[11px] font-medium tracking-wider uppercase">
          Management
        </p>
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            onClick={() => setSection(item.id)}
            className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${
              section === item.id
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
            }`}
          >
            <item.icon className="size-4" />
            {item.label}
          </button>
        ))}
      </nav>

      <div className="border-border border-t p-3">
        <div className="flex items-center justify-between px-2 pb-2">
          <span className="text-muted-foreground text-[11px] font-medium">Theme</span>
          <div className="bg-muted flex items-center gap-0.5 rounded-md p-0.5">
            <button
              onClick={() => setTheme("light")}
              className={`rounded-sm p-1 transition-colors ${
                theme === "light"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Sun className="size-3.5" />
            </button>
            <button
              onClick={() => setTheme("dark")}
              className={`rounded-sm p-1 transition-colors ${
                theme === "dark"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Moon className="size-3.5" />
            </button>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hover:bg-accent flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors">
              <Avatar className="size-8">
                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-1 flex-col text-left">
                <span className="text-foreground truncate text-sm font-medium">
                  {userName || "Admin"}
                </span>
                <span className="text-muted-foreground truncate text-[11px]">
                  {userEmail}
                </span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">{userName || "Admin"}</p>
                <p className="text-muted-foreground text-xs">{userEmail}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              <LogOut className="mr-2 size-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  )
}
